let logoutInProgress = false
let authCheckIntervalId = null
let listenersAttached = false

const APP_BASE_PATH = String(process.env.PUBLIC_URL || "").replace(/\/$/, "")
const LOGIN_PATH = `${APP_BASE_PATH}/login` || "/login"
const AUTH_SESSION_STARTED_KEY = "ps_auth_started_at"
const AUTH_LAST_ACTIVITY_KEY = "ps_auth_last_activity"
const DEFAULT_IDLE_TIMEOUT_MS = 15 * 60 * 1000
const DEFAULT_MAX_SESSION_MS = 8 * 60 * 60 * 1000
const ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "mousemove"]

const isBrowser = () => typeof window !== "undefined"

const hasAuthSession = () => {
  if (!isBrowser()) return false
  try {
    return Boolean(window.sessionStorage.getItem("user"))
  } catch {
    return false
  }
}

const toPositiveNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const getIdleTimeoutMs = () => {
  const minutes = toPositiveNumber(process.env.REACT_APP_AUTH_IDLE_MINUTES)
  return minutes ? minutes * 60 * 1000 : DEFAULT_IDLE_TIMEOUT_MS
}

const getMaxSessionMs = () => {
  const hours = toPositiveNumber(process.env.REACT_APP_AUTH_MAX_HOURS)
  return hours ? hours * 60 * 60 * 1000 : DEFAULT_MAX_SESSION_MS
}

const setTimestamp = (key, value) => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // ignore storage errors
  }
}

const getTimestamp = (key) => {
  if (!isBrowser()) return null
  try {
    const value = Number(window.localStorage.getItem(key))
    if (!Number.isFinite(value) || value <= 0) return null
    return value
  } catch {
    return null
  }
}

const resetThemeToLight = () => {
  if (!isBrowser()) return
  try {
    document.documentElement.setAttribute("data-bs-theme", "light")
  } catch {
    // ignore DOM errors
  }
  try {
    window.localStorage.setItem("darkmode", JSON.stringify(false))
  } catch {
    // ignore storage errors
  }
}

const callServerLogout = async () => {
  const apiBase = String(process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "")

  try {
    await fetch(`${apiBase}/login/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
  } catch {
    // ignore network errors during logout
  }
}

export const touchAuthActivity = () => {
  setTimestamp(AUTH_LAST_ACTIVITY_KEY, Date.now())
}

export const initAuthSession = () => {
  if (!isBrowser()) return

  const now = Date.now()
  const startedAt = getTimestamp(AUTH_SESSION_STARTED_KEY)

  if (!startedAt) {
    setTimestamp(AUTH_SESSION_STARTED_KEY, now)
  }

  touchAuthActivity()
}

const isOnPublicAuthRoute = () => {
  if (!isBrowser()) return false
  const path = String(window.location.pathname || "").toLowerCase()
  return path.startsWith(LOGIN_PATH) || path.includes("/pw-reset") || path.includes("/pw-new/")
}

const evaluateSessionTimeout = () => {
  if (!isBrowser()) return
  if (!hasAuthSession()) return
  if (isOnPublicAuthRoute()) return

  const now = Date.now()
  const startedAt = getTimestamp(AUTH_SESSION_STARTED_KEY) ?? now
  const lastActivity = getTimestamp(AUTH_LAST_ACTIVITY_KEY) ?? now

  const maxSessionExceeded = now - startedAt >= getMaxSessionMs()
  const idleExceeded = now - lastActivity >= getIdleTimeoutMs()

  if (maxSessionExceeded || idleExceeded) {
    forceLogout()
  }
}

const handleActivity = () => {
  if (!hasAuthSession()) return
  touchAuthActivity()
}

const attachActivityListeners = () => {
  if (!isBrowser() || listenersAttached) return
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleActivity, { passive: true })
  })
  listenersAttached = true
}

const detachActivityListeners = () => {
  if (!isBrowser() || !listenersAttached) return
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleActivity)
  })
  listenersAttached = false
}

export const startAuthSessionMonitor = () => {
  if (!isBrowser()) return () => {}

  initAuthSession()
  attachActivityListeners()
  evaluateSessionTimeout()

  if (!authCheckIntervalId) {
    authCheckIntervalId = window.setInterval(evaluateSessionTimeout, ACTIVITY_CHECK_INTERVAL_MS)
  }

  return stopAuthSessionMonitor
}

export const stopAuthSessionMonitor = () => {
  if (!isBrowser()) return
  if (authCheckIntervalId) {
    window.clearInterval(authCheckIntervalId)
    authCheckIntervalId = null
  }
  detachActivityListeners()
}

export const clearAuthState = (options = {}) => {
  const { resetStorage = true } = options

  if (!isBrowser()) return

  if (resetStorage) {
    try {
      window.sessionStorage.clear()
    } catch {
      // ignore storage errors
    }
    try {
      window.localStorage.clear()
    } catch {
      // ignore storage errors
    }

    resetThemeToLight()
  }
}

export const forceLogout = async (options = {}) => {
  const { redirect = true, server = true } = options
  stopAuthSessionMonitor()

  if (server) {
    await callServerLogout()
  }

  clearAuthState()

  if (!redirect || !isBrowser()) return
  if (logoutInProgress) return
  logoutInProgress = true

  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (!current.startsWith(LOGIN_PATH)) {
    window.location.replace(LOGIN_PATH)
    return
  }

  window.location.reload()
}

export const shouldForceLogout = (status, requestPath = "") => {
  if (status !== 401 && status !== 403) return false
  const path = String(requestPath || "").toLowerCase()
  if (path.startsWith("/login")) return false
  if (path.includes("/login/")) return false
  return true
}
